import { queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type { AuditEntry, PagedResult } from './types'

export interface AuditTrailQuery {
  /** Entity name as the server records it, e.g. `LoanCycle`, `Customer`, `TellerSession`. */
  entityType: string
  /** External identifier of the row whose history is being read. */
  entityId: string
  page?: number
  pageSize?: number
}

/**
 * Audit entries for one entity, newest first.
 *
 * The trail is append-only and hash-anchored on the server; nothing here can edit it, and the
 * endpoint is gated on `admin.audit.view`. Reading an audit trail is itself an auditable action,
 * so this query is never fired speculatively — only when a drawer is actually opened.
 */
export const auditTrailQueryOptions = ({ entityType, entityId, page = 1, pageSize = 25 }: AuditTrailQuery) =>
  queryOptions({
    queryKey: ['audit', entityType, entityId, page, pageSize] as const,
    queryFn: async () => {
      // The trail is addressed by path, not query: /access/audit/{entityType}/{entityId}. This
      // client shipped pointing at a route that never existed and every drawer answered 404 --
      // the drift gate checks types, not paths, so only a human clicking found it.
      const response = await apiClient.get<PagedResult<AuditEntry>>(
        `/access/audit/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
        { params: { page, pageSize } },
      )
      return response.data
    },
    staleTime: 0,
  })
